import { APPLE_PRODUCT_IDS, getAppleProductId } from "@/lib/payments/iap-products";
import { iapDebug, iapDebugWarn } from "@/lib/payments/iap-debug";

export const IAP_PLAN_IDS = [
  "premium-monthly",
  "premium-yearly",
  "elite-monthly",
  "elite-yearly",
] as const;

export const EXPECTED_APPLE_PRODUCT_IDS = Object.freeze(Object.values(APPLE_PRODUCT_IDS));

const STOREKIT_RETRY_DELAYS_MS = [0, 1200, 2500, 4000] as const;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export type IapCatalogSnapshot = {
  loaded: boolean;
  /** True when store.products is empty after all retry attempts. */
  storeEmpty: boolean;
  returnedProductIds: string[];
  missingExpectedProductIds: string[];
  /** Whether StoreKit exposed a purchasable offer for each plan. */
  planOfferByPlanId: Record<string, boolean>;
  /** Localized price from StoreKit when available (formatted). */
  localizedPriceByPlanId: Partial<Record<string, string>>;
  /** Set when catalog fetch threw (distinct from empty StoreKit response). */
  fetchError?: string;
};

function pickOffer(product: any) {
  if (!product) return null;
  return product.getOffer?.() ?? product.offers?.[0] ?? product.getOffer?.("$");
}

function pickFormattedPrice(offer: any, product: any): string | undefined {
  const pricing = offer?.pricing ?? product?.pricing;
  const formatted = pricing?.formattedPrice;
  if (typeof formatted === "string") return formatted;
  const price = pricing?.price;
  if (typeof price === "string") return price;
  if (typeof price === "number") return String(price);
  return undefined;
}

/** When store `initialize()` fails or JS throws before a real StoreKit response. */
export function createEmptyIapCatalogSnapshot(fetchError: string): IapCatalogSnapshot {
  const planOfferByPlanId: Record<string, boolean> = {};
  for (const planId of IAP_PLAN_IDS) {
    planOfferByPlanId[planId] = false;
  }
  return {
    loaded: true,
    storeEmpty: true,
    returnedProductIds: [],
    missingExpectedProductIds: [...EXPECTED_APPLE_PRODUCT_IDS],
    planOfferByPlanId,
    localizedPriceByPlanId: {},
    fetchError,
  };
}

/**
 * Registers subscription products, refreshes the StoreKit catalog with retries,
 * and summarizes offers per plan (for UI + gating).
 */
export async function fetchStoreKitCatalogSnapshot(CdvPurchase: any): Promise<IapCatalogSnapshot> {
  try {
    return await fetchStoreKitCatalogSnapshotInner(CdvPurchase);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    iapDebugWarn("fetchStoreKitCatalogSnapshot threw before snapshot", { error: msg });
    return createEmptyIapCatalogSnapshot(msg);
  }
}

async function fetchStoreKitCatalogSnapshotInner(CdvPurchase: any): Promise<IapCatalogSnapshot> {
  const store = CdvPurchase.store;
  const applePlatform = CdvPurchase.Platform.APPLE_APPSTORE;
  const subscriptionProducts = Object.values(APPLE_PRODUCT_IDS).map((id) => ({
    id,
    platform: applePlatform,
    type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
  }));
  store.register(subscriptionProducts);

  let returnedProductIds: string[] = [];
  for (let attempt = 0; attempt < STOREKIT_RETRY_DELAYS_MS.length; attempt++) {
    const delay = STOREKIT_RETRY_DELAYS_MS[attempt];
    if (delay > 0) {
      await sleep(delay);
    }
    iapDebug("Product catalog refresh (store.update)", {
      attempt: attempt + 1,
      maxAttempts: STOREKIT_RETRY_DELAYS_MS.length,
      expectedAppleProductIds: [...EXPECTED_APPLE_PRODUCT_IDS],
    });
    await store.update();
    const products = store.products;
    returnedProductIds = Array.isArray(products)
      ? products
          .map((p: { id?: string }) => p?.id)
          .filter((id): id is string => typeof id === "string" && id.length > 0)
      : [];
    const idSet = new Set(returnedProductIds as string[]);
    const missingExpected = EXPECTED_APPLE_PRODUCT_IDS.filter((id) => !idSet.has(id));
    iapDebug("Catalog vs expected product IDs", {
      attempt: attempt + 1,
      expectedCount: EXPECTED_APPLE_PRODUCT_IDS.length,
      returnedCount: returnedProductIds.length,
      returnedIds: returnedProductIds,
      missingExpectedProductIds: missingExpected,
    });
    if (Array.isArray(products) && products.length > 0) {
      const p0 = products[0] as { id?: string; getOffer?: unknown; offers?: unknown };
      iapDebug("store.products[0] probe (plugin)", {
        id: p0?.id,
        hasGetOffer: typeof p0?.getOffer === "function",
        offersLength: Array.isArray(p0?.offers) ? p0.offers.length : undefined,
      });
    }
    if (missingExpected.length > 0) {
      iapDebugWarn(
        "StoreKit did not return some registered product IDs (wrong ASC IDs, pending review, or propagation delay).",
        { missingExpectedProductIds: missingExpected },
      );
    }
    if (returnedProductIds.length > 0) {
      break;
    }
  }

  const storeEmpty = returnedProductIds.length === 0;
  const planOfferByPlanId: Record<string, boolean> = {};
  const localizedPriceByPlanId: Partial<Record<string, string>> = {};

  for (const planId of IAP_PLAN_IDS) {
    const appleId = getAppleProductId(planId);
    if (!appleId) {
      planOfferByPlanId[planId] = false;
      continue;
    }
    const product = store.get(appleId, applePlatform) ?? store.get(appleId);
    const offer = pickOffer(product);
    planOfferByPlanId[planId] = !!offer;
    const formatted = pickFormattedPrice(offer, product);
    if (formatted) {
      localizedPriceByPlanId[planId] = formatted;
    }
  }

  const idSet = new Set(returnedProductIds as string[]);
  const missingExpectedProductIds = EXPECTED_APPLE_PRODUCT_IDS.filter((id) => !idSet.has(id));

  return {
    loaded: true,
    storeEmpty,
    returnedProductIds: returnedProductIds as string[],
    missingExpectedProductIds,
    planOfferByPlanId,
    localizedPriceByPlanId,
  };
}
